/**
 * Main application entry point - VERSION PROPRE SANS SYSTÈME DE SÉCURITÉ COMPLEXE
 */

import { HeroBanner } from './components/HeroBanner';
import { HeaderComponent } from './components/HeaderComponent';

import { InformationalSectionsManager } from './components/InformationalSectionsManager';
// import { ValidationManager } from './utils/ValidationManager'; // Removed
import { LazyLoader } from './utils/LazyLoader';
import { ServiceWorkerManager } from './utils/ServiceWorkerManager';
import { ImageOptimizer } from './utils/ImageOptimizer';
import { MobileOptimizer } from './utils/MobileOptimizer';
// import { ResponsiveTestSuite } from './utils/ResponsiveTestSuite'; // Removed
import { initializeAccessibility } from './accessibility-init.js';
import { initSimpleSearch } from './components/SimpleSearchHandler.js';

/**
 * Main application class
 */
class KiwiTravelApp {
  private heroBanner: HeroBanner | null = null;
  private headerComponent: HeaderComponent | null = null;

  private informationalSectionsManager: InformationalSectionsManager | null = null;
  // private validationManager: ValidationManager | null = null; // Removed

  private mobileOptimizer: MobileOptimizer | null = null;
  // private responsiveTestSuite: ResponsiveTestSuite | null = null; // Removed

  constructor() {
    this.init();
  }

  /**
   * Initialize the application
   */
  private async init(): Promise<void> {
    try {
      console.log('🚀 Initializing Kiwi Travel App...');

      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        await new Promise(resolve => {
          document.addEventListener('DOMContentLoaded', resolve);
        });
      }

      // Initialize accessibility features first
      initializeAccessibility({
        enableSkipLinks: true,
        enableAriaAnnouncements: true,
        enableKeyboardNavigation: true,
        enableHighContrast: true,
        enableReducedMotion: true,
        enableFocusManagement: true
      });

      // Initialize core components
      await this.onDOMReady();

      console.log('✅ Kiwi Travel App initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize app:', error);
    }
  }

  /**
   * Detect if we're on the homepage
   */
  private detectHomepage(): boolean {
    const path = window.location.pathname;
    return path === '/' || path === '/index.html' || path.endsWith('/index.html');
  }

  /**
   * Handle DOM ready event
   */
  private async onDOMReady(): Promise<void> {
    this.initializeComponents();
    this.attachGlobalEventListeners();
    this.setupAccessibility();
    this.setupSEO();
    this.setupImageFallbacks();

    // Initialize mobile optimizer after DOM is ready
    if (this.mobileOptimizer) {
      await this.mobileOptimizer.init();
    }

    // Initialize responsive test suite in development - Removed

    // Initialize service worker
    ServiceWorkerManager.init();

    // Initialize lazy loading
    LazyLoader.init();

    // Optimize images
    ImageOptimizer.init();

    console.log('🎉 DOM ready and components initialized');
  }

  /**
   * Initialize all components
   */
  private initializeComponents(): void {
    try {
      // Initialize header component (present on all pages)
      this.headerComponent = new HeaderComponent();

      // Initialize homepage-specific components
      if (this.detectHomepage()) {
        console.log('🏠 Homepage detected, initializing homepage components...');

        // Initialize hero banner
        const heroElement = document.querySelector('.hero-banner') as HTMLElement;
        if (heroElement) {
          this.heroBanner = new HeroBanner(heroElement);
        }

        // Initialize simple search system
        this.initializeSimpleSearch();





        // Initialize informational sections
        this.informationalSectionsManager = new InformationalSectionsManager();
        this.informationalSectionsManager.init();
      }

      // Initialize validation manager - Removed

      // Initialize mobile optimizer
      this.mobileOptimizer = new MobileOptimizer();

      // Initialize responsive test suite in development - Removed

      console.log('✅ Components initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize components:', error);
    }
  }

  /**
   * Attach global event listeners
   */
  private attachGlobalEventListeners(): void {
    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        console.log('📱 Page hidden');
      } else {
        console.log('📱 Page visible');
      }
    });

    // Handle online/offline status
    window.addEventListener('online', () => {
      console.log('🌐 Back online');
    });

    window.addEventListener('offline', () => {
      console.log('📴 Gone offline');
    });

    // Handle errors
    window.addEventListener('error', (event) => {
      console.error('Global error:', event.error);
    });

    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
    });
  }

  /**
   * Setup accessibility features
   */
  private setupAccessibility(): void {


    // Announce page changes to screen readers
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    announcer.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    `;
    document.body.appendChild(announcer);
  }

  /**
   * Setup SEO enhancements
   */
  private setupSEO(): void {
    // Add canonical URL if not present
    if (!document.querySelector('link[rel="canonical"]')) {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = window.location.href;
      document.head.appendChild(canonical);
    }

    // Add structured data for homepage
    if (this.detectHomepage()) {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "TravelAgency",
        "name": "Kiwi Travel",
        "description": "Book your flights, hotels and trains at the best price",
        "url": window.location.origin,
        "sameAs": [
          "https://www.facebook.com/kiwitravel",
          "https://www.twitter.com/kiwitravel",
          "https://www.instagram.com/kiwitravel"
        ]
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }

  /**
   * Setup image fallbacks
   */
  private setupImageFallbacks(): void {
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      img.addEventListener('error', () => {
        // Skip if image has inline onerror handler (like tips and offers)
        if (!img.dataset.fallbackApplied && !img.getAttribute('onerror')) {
          // Try to get city name from alt text or data attributes
          const altText = img.alt.toLowerCase();
          const cityMatch = altText.match(/^([^,]+)/); // Get first part before comma

          if (cityMatch && cityMatch[1]) {
            const cityName = cityMatch[1].trim().replace(/\s+/g, '-');
            const cityImagePath = `/assets/images/destinations/${cityName}.jpg`;

            // Try city-specific image first
            const testImg = new Image();
            testImg.onload = () => {
              img.src = cityImagePath;
              img.dataset.fallbackApplied = 'true';
            };
            testImg.onerror = () => {
              img.src = '/assets/images/placeholder.jpg';
              img.alt = 'Image not available';
              img.dataset.fallbackApplied = 'true';
            };
            testImg.src = cityImagePath;
          } else {
            img.src = '/assets/images/placeholder.jpg';
            img.alt = 'Image not available';
            img.dataset.fallbackApplied = 'true';
          }
        }
      });
    });
  }

  /**
   * Initialize simple search system
   */
  private initializeSimpleSearch(): void {
    try {
      console.log('🔍 Initializing simple search system...');

      // Initialiser le gestionnaire de recherche simple
      initSimpleSearch();

      console.log('✅ Simple search system initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize simple search system:', error);
    }
  }



  /**
   * Cleanup resources
   */
  public destroy(): void {
    if (this.heroBanner) {
      // this.heroBanner.destroy(); // Method doesn't exist
      this.heroBanner = null;
    }

    if (this.headerComponent) {
      this.headerComponent.destroy();
      this.headerComponent = null;
    }



    if (this.informationalSectionsManager) {
      this.informationalSectionsManager.destroy();
      this.informationalSectionsManager = null;
    }

    // Validation manager removed

    if (this.mobileOptimizer) {
      this.mobileOptimizer.destroy();
      this.mobileOptimizer = null;
    }

    // Responsive test suite removed

    console.log('🧹 App resources cleaned up');
  }
}

// Initialize the application
const app = new KiwiTravelApp();

// Make app available globally for debugging
(window as any).kiwiApp = app;

// Handle page unload
window.addEventListener('beforeunload', () => {
  app.destroy();
});

export default KiwiTravelApp;